/**
 * Management Page - renamed from EmployeeManagementPage
 * Contains Employee and Shifts management tabs
 */
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, Users, UserCheck, UserX, Calendar } from "lucide-react";
import { useEmployeeManagement } from "./hooks/useEmployeeManagement";
import { EmployeeDialogs } from "./components/EmployeeDialogs";
import { EmployeeTabs } from "./components/EmployeeTabs";
import { UpdateShiftPage } from "@/components/shifts/UpdateShiftPage";
import { EmployeeModal } from "@/components/employee/EmployeeModal";
import { DutyRosterEmployeesList } from "@/components/employee/sections/DutyRosterEmployeesList";
import { DepartmentMetric } from "@/types/enums";

const ManagementPage = () => {
  const [activeTab, setActiveTab] = useState<string>(DepartmentMetric.EMPLOYEES);
  
  // ✅ UPDATED: Use centralized state from useEmployeeManagement
  const {
    // Centralized state
    employees,
    activeEmployees,
    isLoadingEmployees,
    employeesError,
    
    // UI state
    isProfileDialogOpen,
    isEditDialogOpen,
    isOvertimeDialogOpen,
    isChangeDeptDialogOpen,
    isChangeStatusDialogOpen,
    selectedEmployee,
    departments,
    isLoadingDepartments,
    acceptedDomains,
    
    // Action handlers
    viewEmployeeProfile,
    handleEditUser,
    viewOvertimeHistory,
    openChangeDepartmentDialog,
    openChangeStatusDialog,
    createNewUser,
    saveEditedUser,
    saveDepartmentChange,
    saveStatusChange,
    handleProfileDialogClose,
    handleEditDialogClose,
    handleOvertimeDialogClose,
    handleChangeDeptDialogClose,
    handleChangeStatusDialogClose,
    isNewUserDialogOpen,
    handleNewUserDialogClose,
    // NEW: Duty roster utility functions
    getDutyRosterEmployees,
    getNonDutyRosterEmployees
  } = useEmployeeManagement();


  // Calculate duty roster statistics
  const dutyRosterEmployees = getDutyRosterEmployees();
  const nonDutyRosterEmployees = getNonDutyRosterEmployees();

  // ✅ NEW: Wrapper functions to handle the reason parameter expected by EmployeeDialogs
  const handleDepartmentChangeWithReason = (userId: string, newDepartment: string) => {
    saveDepartmentChange(userId, newDepartment);
  };

  const handleStatusChangeWithReason = (userId: string, newStatus: string) => {
    saveStatusChange(userId, newStatus);
  };

  // ✅ NEW: Show error if employee loading fails
  if (employeesError) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error Loading Employees</h3>
          <p className="text-red-600 mt-2">{employeesError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Tabs 
        defaultValue={DepartmentMetric.EMPLOYEES} 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="w-full"
      >
        {/* Header with Tabs and New Employee Button */}
        <div className="flex items-center justify-between">
          <TabsList className="grid w-full max-w-md grid-cols-3 bg-background border border-border shadow-sm">
            <TabsTrigger 
              value={DepartmentMetric.EMPLOYEES} 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all duration-200"
            >
              Employees
            </TabsTrigger>
            <TabsTrigger 
              value="groups" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all duration-200"
            >
              Groups
            </TabsTrigger>
            <TabsTrigger 
              value="shifts" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all duration-200"
            >
              Shifts
            </TabsTrigger>
          </TabsList>
          
          {/* New Employee Button - Right Aligned */}
          <Button
            onClick={() => handleNewUserDialogClose(true)}
            disabled={isLoadingDepartments || departments.length === 0}
            variant="secondary"
            className="px-4 py-2 rounded-lg shadow-sm transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            size="sm"
          >
            <Plus className="h-4 w-4" />
            {isLoadingDepartments ? "Loading..." : "New Employee"}
          </Button>
        </div>
        
        {/* Tab Content - EXACT same spacing as settings page */}
        <div className="mt-6">
          <TabsContent value={DepartmentMetric.EMPLOYEES} className="space-y-6">
            {/* ✅ NEW: Employee Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/50 p-4 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Employees</p>
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{employees.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/50 dark:to-green-900/50 p-4 rounded-lg border border-green-200/50 dark:border-green-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <UserCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">Active Employees</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">{activeEmployees.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/50 dark:to-purple-900/50 p-4 rounded-lg border border-purple-200/50 dark:border-purple-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-purple-700 dark:text-purple-300">Duty Roster</p>
                    <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{dutyRosterEmployees.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/50 dark:to-amber-900/50 p-4 rounded-lg border border-amber-200/50 dark:border-amber-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <UserX className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Regular Shift</p>
                    <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">{nonDutyRosterEmployees.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Employee Management Tabs */}
            <EmployeeTabs
              onViewProfile={viewEmployeeProfile}
              onEdit={handleEditUser}
              onViewOvertimeHistory={viewOvertimeHistory}
              onChangeDepartment={openChangeDepartmentDialog}
              onChangeStatus={openChangeStatusDialog}
              isNewUserDialogOpen={isNewUserDialogOpen}
              onNewUserOpenChange={handleNewUserDialogClose}
              departments={departments}
              isLoadingDepartments={isLoadingDepartments}
              acceptedDomains={acceptedDomains}
              createNewUser={createNewUser}
              // ✅ NEW: Pass centralized state
              employees={employees}
              isLoadingEmployees={isLoadingEmployees}
              employeesError={employeesError}
            />
          </TabsContent>
          
          <TabsContent value="shifts" className="space-y-6">
            <UpdateShiftPage />
          </TabsContent>
          
          <TabsContent value="groups" className="space-y-6">
            <DutyRosterEmployeesList
              users={employees}
              isLoading={isLoadingEmployees}
              onViewProfile={viewEmployeeProfile}
              onEdit={handleEditUser}
              onViewOvertimeHistory={viewOvertimeHistory}
              onChangeStatus={openChangeStatusDialog}
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* Employee Dialogs */}
      <EmployeeDialogs
        isProfileDialogOpen={isProfileDialogOpen}
        isEditDialogOpen={isEditDialogOpen}
        isOvertimeDialogOpen={isOvertimeDialogOpen}
        isChangeDeptDialogOpen={isChangeDeptDialogOpen}
        isChangeStatusDialogOpen={isChangeStatusDialogOpen}
        selectedEmployee={selectedEmployee}
        departments={departments}
        isLoadingDepartments={isLoadingDepartments}
        acceptedDomains={acceptedDomains}
        handleProfileDialogClose={handleProfileDialogClose}
        handleEditDialogClose={handleEditDialogClose}
        handleOvertimeDialogClose={handleOvertimeDialogClose}
        handleChangeDeptDialogClose={handleChangeDeptDialogClose}
        handleChangeStatusDialogClose={handleChangeStatusDialogClose}
        saveEditedUser={saveEditedUser}
        saveDepartmentChange={handleDepartmentChangeWithReason}
        saveStatusChange={handleStatusChangeWithReason}
      />

      {/* New Employee Modal */}
      <EmployeeModal
        mode="add"
        open={isNewUserDialogOpen}
        onOpenChange={handleNewUserDialogClose}
        onSave={createNewUser}
        departments={departments}
        isLoadingDepartments={isLoadingDepartments}
        acceptedDomains={acceptedDomains}
      />
    </div>
  );
};

export default ManagementPage;
